import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { HV_TAKEOVER_GRID_ID } from '@/lib/constants/addresses'
import { classicAppUrl } from '@/lib/classic-app'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number as USD value
 * @param amount - The amount to format (bigint assumes 6 decimals for USDC)
 * @param options.showSign - If true, shows + or - prefix
 * @param options.showDollar - If true, includes $ symbol
 */
export function formatUSD(amount: number | bigint, options?: { showSign?: boolean; showDollar?: boolean }): string {
  const num = typeof amount === 'bigint' ? Number(amount) / 1e6 : amount
  
  // Default to showing $ symbol for USDC values
  const dollar = options?.showDollar !== false ? '$' : ''
  
  if (num > 0 && num < 0.01) {
    return `<${dollar}0.01`
  }
  
  if (num < 0 && num > -0.01) {
    return `-<${dollar}0.01`
  }
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(num))
  
  if (options?.showSign && num !== 0) {
    return num > 0 ? `+${dollar}${formatted}` : `-${dollar}${formatted}`
  }
  return num < 0 ? `-${dollar}${formatted}` : `${dollar}${formatted}`
}

/**
 * Compact USD: $12.45K for <100, $132.3K for >=100, $1.2M, $3.5B etc.
 */
export function formatUSDCompact(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}$${parseFloat((abs / 1_000_000_000).toFixed(1))}B`;
  if (abs >= 100_000_000) return `${sign}$${parseFloat((abs / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000_000) return `${sign}$${parseFloat((abs / 1_000_000).toFixed(2))}M`;
  if (abs >= 100_000) return `${sign}$${parseFloat((abs / 1_000).toFixed(1))}K`;
  if (abs >= 1_000) return `${sign}$${parseFloat((abs / 1_000).toFixed(2))}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

/**
 * Compact USD formatter used by market/grid list UIs.
 * Preserves "Free" label behavior for zero values when requested.
 */
export function formatMarketUsd(
  value: number,
  options?: { freeLabel?: boolean }
): string {
  if (value >= 1_000_000_000) {
    return `$${parseFloat((value / 1_000_000_000).toFixed(1))}B`;
  }
  if (value >= 1_000_000) {
    return `$${parseFloat((value / 1_000_000).toFixed(2))}M`;
  }
  if (value >= 100_000) return `$${Math.round(value / 1_000)}K`;
  if (value >= 1_000) return `$${parseFloat((value / 1_000).toFixed(2))}K`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value > 0) return `$${value.toFixed(4)}`;
  return options?.freeLabel ? "Free" : "$0";
}

/**
 * Backward-compatible alias for existing market page usage.
 */
export const formatUsdMarketValue = formatMarketUsd;

/**
 * Format a raw on-chain token amount (string) for claim/fee display.
 * Uses K suffix for thousands and variable precision (4dp < 1, 3dp 1-10, 2dp 10+).
 */
export function formatTokenAmount(raw: string, decimals: number): string {
  const val = Number(raw) / 10 ** decimals;
  if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(1)}B`;
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  if (val >= 10) return val.toFixed(2);
  if (val >= 1) return val.toFixed(3);
  if (val > 0) return val.toFixed(4);
  return '0';
}

/**
 * Format a token amount with compact K/M/B suffixes.
 * E.g. 1240 -> "1.24K", 2500000 -> "2.5M". Optionally appends a token symbol.
 */
export function formatTokenCompact(amount: number, symbol?: string): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  let formatted: string;
  if (abs >= 1_000_000_000) formatted = `${sign}${parseFloat((abs / 1_000_000_000).toFixed(2))}B`;
  else if (abs >= 1_000_000) formatted = `${sign}${parseFloat((abs / 1_000_000).toFixed(2))}M`;
  else if (abs >= 1_000) formatted = `${sign}${parseFloat((abs / 1_000).toFixed(2))}K`;
  else if (abs >= 1) formatted = `${sign}${abs.toFixed(2)}`;
  else if (abs > 0) formatted = `${sign}${abs.toFixed(4)}`;
  else formatted = '0';
  return symbol ? `${formatted} ${symbol}` : formatted;
}

/**
 * Format USDC amount (6 decimals) to human readable
 */
export function formatUSDC(amount: bigint): string {
  return formatUSD(amount)
}

/**
 * Format ETH amount (18 decimals) to human readable
 * @param amount - The amount in wei (bigint)
 * @param options.showSymbol - If true, shows ETH suffix (default true)
 * @param options.decimals - Number of decimal places (default 4)
 */
export function formatETH(amount: bigint, options?: { showSymbol?: boolean; decimals?: number }): string {
  const decimals = options?.decimals ?? 4
  const num = Number(amount) / 1e18
  const symbol = options?.showSymbol !== false ? ' ETH' : ''
  
  // Show "<0.0001 ETH" for very small amounts
  if (num > 0 && num < 0.0001) {
    return `<0.0001${symbol}`
  }
  
  // For small but displayable amounts, show more precision
  const effectiveDecimals = num > 0 && num < 0.01 ? 6 : decimals
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: effectiveDecimals,
  }).format(num)
  
  return `${formatted}${symbol}`
}

/**
 * Format a duration in seconds to human readable
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600)
    return `${hours}h`
  }
  const days = Math.floor(seconds / 86400)
  return `${days}d`
}

/**
 * Format time until forfeiture
 * Shows days by default, switches to whole weeks (rounded up) above 30 days
 */
export function formatRunway(seconds: number): string {
  if (seconds <= 0) return 'Forfeited'
  
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  
  if (days > 30) {
    const weeks = Math.ceil(days / 7)
    return `${weeks} weeks`
  }
  if (days > 0) {
    return `${days}d ${hours}h`
  }
  if (hours > 0) {
    return `${hours} hours`
  }
  const minutes = Math.floor(seconds / 60)
  return `${minutes} min`
}

/**
 * Format duration from weeks (float) to human readable
 * Shows days for <= 30 days, whole weeks (rounded up) for > 30 days
 * @param weeks - Duration in weeks (float)
 * @param compact - If true, uses short format: "14d", "5w". If false: "14 days", "5 weeks"
 */
export function formatDurationWeeks(weeks: number, compact = false): string {
  if (weeks <= 0) return '0 seconds'

  const totalSeconds = weeks * 7 * 24 * 60 * 60

  // Under 1 minute: show seconds
  if (totalSeconds < 60) {
    const s = Math.floor(totalSeconds)
    return `${s} second${s !== 1 ? 's' : ''}`
  }

  // Under 1 hour: show minutes
  if (totalSeconds < 3600) {
    const m = Math.floor(totalSeconds / 60)
    return `${m} minute${m !== 1 ? 's' : ''}`
  }

  const days = weeks * 7

  // Under 1 day: show hours (+ minutes in non-compact)
  if (days < 1) {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    if (!compact && m > 0) return `${h} hour${h !== 1 ? 's' : ''} ${m} min`
    return `${h} hour${h !== 1 ? 's' : ''}`
  }

  // Over ~4 weeks: show weeks
  if (days > 30) {
    const w = Math.ceil(weeks)
    return `${w} week${w !== 1 ? 's' : ''}`
  }

  // Otherwise: show days
  const d = Math.round(days)
  return `${Math.max(1, d)} day${d !== 1 ? 's' : ''}`
}

/**
 * Format how long a seat has been held (shows minutes/hours/days appropriately)
 */
export function formatHeldDuration(seconds: number): string {
  if (seconds < 0) return '0m'
  
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(seconds / 3600)
  const days = Math.floor(seconds / 86400)
  
  if (days >= 1) {
    const remainingHours = Math.floor((seconds % 86400) / 3600)
    if (days >= 7) {
      return `${days}d`
    }
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
  }
  if (hours >= 1) {
    const remainingMinutes = Math.floor((seconds % 3600) / 60)
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }
  return `${minutes}m`
}

/**
 * Truncate an address for display
 */
export function truncateAddress(address: string, chars = 4): string {
  if (!address) return ''
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

/**
 * Calculate tax per week based on price and rate
 */
export function calculateWeeklyTax(priceUSDC: bigint, ratePerWeekBps: number): bigint {
  return (priceUSDC * BigInt(ratePerWeekBps)) / 10000n
}

/**
 * Calculate time until forfeiture based on balance and tax rate
 */
export function calculateRunwaySeconds(balanceUSDC: bigint, priceUSDC: bigint, ratePerWeekBps: number): number {
  if (priceUSDC === 0n || ratePerWeekBps === 0) return Infinity
  
  const weeklyTax = calculateWeeklyTax(priceUSDC, ratePerWeekBps)
  if (weeklyTax === 0n) return Infinity
  
  // runway in weeks = balance / weeklyTax
  // convert to seconds
  const runwayWeeks = Number(balanceUSDC) / Number(weeklyTax)
  return runwayWeeks * 7 * 24 * 60 * 60
}

/**
 * Get health status based on runway
 */
export function getHealthStatus(runwaySeconds: number): 'healthy' | 'warning' | 'critical' | 'danger' {
  const days = runwaySeconds / 86400
  if (days > 3) return 'healthy'
  if (days > 2) return 'warning'
  if (days > 1) return 'critical'
  return 'danger'
}

/**
 * Get color class for health status
 */
export function getHealthColor(status: ReturnType<typeof getHealthStatus>): string {
  switch (status) {
    case 'healthy': return 'bg-win-green'
    case 'warning': return 'bg-yellow-500'
    case 'critical': return 'bg-orange-500'
    case 'danger': return 'bg-loss-red'
  }
}

/**
 * Upgrade avatar URL to higher resolution version
 * Handles Twitter/X profile pictures and other common formats
 */
export function getHighResAvatarUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  
  // Twitter/X profile pictures - upgrade to 400x400
  // Format: https://pbs.twimg.com/profile_images/xxx/yyy_normal.jpg
  if (url.includes('pbs.twimg.com') || url.includes('twimg.com')) {
    return url
      .replace(/_normal\./, '_400x400.')
      .replace(/_bigger\./, '_400x400.')
      .replace(/_200x200\./, '_400x400.')
      .replace(/_mini\./, '_400x400.');
  }
  
  // Return as-is for other sources (Farcaster, etc. usually provide good resolution)
  return url;
}

export function formatTimeAgo(timestampMs: number): string {
  const seconds = Math.floor((Date.now() - timestampMs) / 1000);
  if (seconds < 5) return 'now';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function formatTvlUsd(tvlUsd: bigint | string | number): string {
  const dollars = Number(tvlUsd) / 1_000_000;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  if (dollars >= 1) return `$${dollars.toFixed(0)}`;
  if (dollars > 0) return `$${dollars.toFixed(2)}`;
  return '$0';
}

export function formatTvlNative(
  tvlNative: bigint | string | number,
  symbol: string = 'ETH',
  decimals: number = 18,
): string {
  const val = Number(tvlNative) / 10 ** decimals;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K ${symbol}`;
  if (val >= 10) return `${val.toFixed(1)} ${symbol}`;
  if (val >= 1) return `${val.toFixed(2)} ${symbol}`;
  if (val > 0) return `${val.toFixed(4)} ${symbol}`;
  return `0 ${symbol}`;
}

/**
 * Returns true when a click event includes modifier keys or is not a primary
 * button press, indicating the user intends to open the link in a new tab /
 * window rather than performing an in-page navigation.
 */
export function isModifiedClick(e: Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'button'>): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

/**
 * Parse a composite grid entity ID ("{managerAddress}-{numericGridId}" or
 * "{managerAddress}/{numericGridId}" from URL paths) into its components.
 * Falls back gracefully for legacy bare-numeric IDs.
 */
export function parseGridId(compositeId: string): { managerAddress: string; numericId: string } {
  if (!compositeId.startsWith('0x')) {
    return { managerAddress: '', numericId: compositeId };
  }
  // Support both dash (internal/indexer) and slash (URL) separators
  const dashIdx = compositeId.indexOf('-', 2);
  const slashIdx = compositeId.indexOf('/', 2);
  const sepIdx = dashIdx !== -1 ? dashIdx : slashIdx;
  if (sepIdx === -1) {
    return { managerAddress: '', numericId: compositeId };
  }
  return {
    managerAddress: compositeId.slice(0, sepIdx),
    numericId: compositeId.slice(sepIdx + 1),
  };
}

/**
 * Build a board page URL. Returns the classic-app boardroom URL for
 * hvTAKEOVER (the only board still served by the legacy app), otherwise
 * the lobby's `/board/{ugm}-{numericId}` route. The legacy
 * `/grid/{ugm}/{numericId}` shape was retired with the V1 page tree;
 * `middleware.ts` still 307-redirects it to the lobby for old shares,
 * but the app itself emits canonical `/board/...` URLs everywhere now.
 */
export function gridHref(gridId: string): string {
  if (gridId === HV_TAKEOVER_GRID_ID) return classicAppUrl('/boardroom');
  const { managerAddress, numericId } = parseGridId(gridId);
  if (managerAddress) return `/board/${managerAddress}-${numericId}`;
  return `/board/${gridId}`;
}

/**
 * Convert an internal dash-separated grid ID to slash-separated
 * for use in API fetch URLs.
 */
export function gridIdToPath(gridId: string): string {
  const { managerAddress, numericId } = parseGridId(gridId);
  if (managerAddress) return `${managerAddress}/${numericId}`;
  return `v1/${gridId}`;
}
